import React, { Children, FC, forwardRef, Ref } from 'react';
import MuiMenuItem, { MenuItemProps as MuiMenuItemProps } from '@mui/material/MenuItem';
import { ListItemIcon } from '../List/ListItemIcon';
import { ListItemText } from '../List/ListItemText';
import { BpDesignSystem } from '../../types';
import { mergeClassNames } from '../../utils/cssClassNameHelper';
import { IconName } from '../Icon/types';
import { Icon, type IconProps } from '../Icon/Icon';
import type { TypographyProps } from '../Typography';

export type MenuItemProps<COMPONENT extends keyof JSX.IntrinsicElements | FC> = BpDesignSystem.PropsWithChildren<
  MuiMenuItemProps<COMPONENT, { button?: true; component?: COMPONENT }> & {
    dangerousAction?: boolean;
    startIcon?: IconName | React.ReactNode;
    endIcon?: IconName | React.ReactNode;
    secondaryText?: React.ReactNode;
    slotProps?: {
      startIcon?: Omit<IconProps, 'name'>;
      endIcon?: Omit<IconProps, 'name'>;
      listItemText?: TypographyProps;
    };
  }
>;

export const MenuItem = forwardRef<HTMLLIElement, Omit<MenuItemProps<'li'>, 'ref'>>(
  <T extends keyof JSX.IntrinsicElements | FC = 'li'>(
    {
      dataTest,
      dangerousAction,
      className,
      children,
      startIcon,
      endIcon,
      secondaryText,
      slotProps,
      ...props
    }: MenuItemProps<T>,
    ref: Ref<HTMLLIElement>
  ) => {
    const shouldWrap = Children.count(children) === 1 || secondaryText;
    const content = shouldWrap ? (
      <ListItemText slotProps={{ primary: { component: 'div', ...slotProps?.listItemText } }} secondary={secondaryText}>
        {children}
      </ListItemText>
    ) : (
      children
    );
    return (
      <MuiMenuItem
        data-test={dataTest}
        ref={ref}
        className={mergeClassNames(className, {
          'dangerous-action': !!dangerousAction
        })}
        {...props}
      >
        {startIcon && (
          <ListItemIcon>
            {typeof startIcon === 'string' && <Icon name={startIcon as IconName} size={20} {...slotProps?.startIcon} />}
            {typeof startIcon !== 'string' && startIcon}
          </ListItemIcon>
        )}
        {content}
        {endIcon && (
          <ListItemIcon sx={{ justifyContent: 'end' }}>
            {typeof endIcon === 'string' && <Icon name={endIcon as IconName} size={20} {...slotProps?.endIcon} />}
            {typeof endIcon !== 'string' && endIcon}
          </ListItemIcon>
        )}
      </MuiMenuItem>
    );
  }
);
